"use client"

import type React from "react"
import { useState, useEffect, useCallback } from "react"
import { Row, Col, Card, CardBody, Button } from "reactstrap" // Using reactstrap components
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css" // Import react-datepicker styles
import { toast } from "react-toastify"

import ExhibitorDashboardService from "../../../Service/exhibitorDashboardService"
import type { ExhibitorSummaryStats } from "./types"

interface InlineStatsCardProps {
  title: string
  value: number | string
  icon: string
  loading: boolean
  isCurrency?: boolean
}

const InlineStatsCard: React.FC<InlineStatsCardProps> = ({ title, value, icon, loading, isCurrency }) => {
  const formattedValue = isCurrency ? `FRW ${value.toLocaleString()}` : value.toLocaleString()

  return (
    <Card className="shadow-sm border-left-primary py-2">
      <CardBody>
        <Row className="align-items-center no-gutters">
          <Col className="mr-2">
            <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">{title}</div>
            <div className="h5 mb-0 font-weight-bold text-gray-800">{loading ? "Loading..." : formattedValue}</div>
          </Col>
          <Col xs="auto">
            <i className={`${icon} fa-2x text-gray-300`}></i>
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
}

const ExhibitorStatsCard: React.FC = () => {
  const [stats, setStats] = useState<ExhibitorSummaryStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)

  const fetchSummaryStats = useCallback(async (start?: Date | null, end?: Date | null) => {
    setLoading(true)
    try {
      const data = await ExhibitorDashboardService.getExhibitorSummaryStats(start || undefined, end || undefined)
      setStats(data)
    } catch (error) {
      toast.error("Failed to load exhibitor summary stats.")
      console.error("Error fetching exhibitor summary stats:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSummaryStats(startDate, endDate)
  }, [fetchSummaryStats, startDate, endDate])

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    setStartDate(start)
    setEndDate(end)
  }

  return (
    <Row className="g-4 mb-4">
        
      <Col xl="3" md="6">
        <InlineStatsCard
          title="Total Orders"
          value={stats?.totalOrders || 0}
          icon="fa fa-shopping-cart"
          loading={loading}
        />
      </Col>
      <Col xl="3" md="6">
        <InlineStatsCard
          title="Total Amount Paid"
          value={stats?.totalAmountPaid || 0}
          icon="fa fa-money"
          isCurrency
          loading={loading}
        />
      </Col>
      <Col xl="3" md="6">
        <InlineStatsCard
          title="Total Participants"
          value={stats?.totalParticipants || 0}
          icon="fa fa-users"
          loading={loading}
        />
      </Col>
      <Col xl="3" md="6">
        <InlineStatsCard title="Total Cars" value={stats?.totalCars || 0} icon="fa fa-car" loading={loading} />
      </Col>
    </Row>
  )
}

export default ExhibitorStatsCard
